import { LOADING_STATUSES } from 'constants/loadingStatuses'

import { useCallback, useEffect, useRef } from 'react'

import styled from 'styled-components'
import { PublicLinkPasswordModal } from 'features/public-link/PublicLinkPasswordModal'
import { useAppDispatch } from  "store"
import { useNavigate, useParams } from 'react-router-dom'
import { PublicLinkPlaceholder, PublicLinkPlaceholderBtnTypes } from 'features/public-link/PublicLinkPlaceholder'
import { useTranslation } from 'react-i18next'
import { PageTopbar } from 'features/common/page-topbar'
import { useMobileDetection } from 'features/common/hooks'
import { usePageTitle } from 'features/common/hooks/usePageTitle'
import { FilesSkeleton } from 'features/files/FilesSkeleton'
import {
  fetchFilesPublicLinkInfoThunk,
  fetchFilesPublicLinkItems,
  fetchPublicLinkWithMeBreadcrumbsThunk,
  FilesPublicLinksErrorTypes,
  publicLinkFilesActions,
  unsubscribeFilesPublicLinkToWsThunk
} from 'features/public-link-files/publicLinkFilesSlice'
import {
  getFilesPublicLinkErrorSelector,
  getFilesPublicLinkLoadingStatusSelector,
  getFilesPublicLinkNodesHrefSelector,
  getFilesPublicLinkSelectedItemsCountSelector,
  getIsSizeQuotaExceedErrorSelector,
  getFilesPublicLinkBreadcrumbsSelector,
  getFilesPublicLinkPermissionSelector
} from 'features/public-link-files/selectors'
import { PublicFiles } from 'features/public-link-files/PublicFiles'
import { parsePathFromParams } from 'features/common/parsePathFromParams/parsePathFromParams'
import { SDK_TYPES } from 'sdk/sdkConstants'
import { FilesTableBreadcrumbs, STYLED_VARIABLES } from '@cloudike/web_ui_components'
import classNames from 'classnames'
import { MobileToolbarMenuToggle } from 'features/common/right-sidebar/MobileToolbarMenuToggle'
import { filesActions } from 'features/files/filesSlice'

import { hideNotification, NOTIFICATION_TYPES, showNotification } from "../../../features/common/notifications"
import { goToSubscriptionsPage } from "../../../utils/subscriptions"
import { useResizeWindow } from "../../../features/common/hooks/useResizeWindow"

function FilesPage() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const params = useParams()
  const isMobile = useMobileDetection()

  const error = getFilesPublicLinkErrorSelector()
  const selectedItemsCount = getFilesPublicLinkSelectedItemsCountSelector()
  const status = getFilesPublicLinkLoadingStatusSelector()
  const nodesHref = getFilesPublicLinkNodesHrefSelector()
  const breadcrumbs = getFilesPublicLinkBreadcrumbsSelector()
  const widthWindow = useResizeWindow()
  const permission = getFilesPublicLinkPermissionSelector()

  usePageTitle('a_nav_publicFiles')

  const paths = parsePathFromParams(params, 'drive')
  const rootId = paths[0]

  useEffect(() => {
    dispatch(fetchFilesPublicLinkInfoThunk({ id: rootId, navigate }))

    return () => {
      dispatch(unsubscribeFilesPublicLinkToWsThunk())
      dispatch(publicLinkFilesActions.setNodeData(null))
      dispatch(publicLinkFilesActions.setItemsHref(null))
      dispatch(publicLinkFilesActions.setItemsIdsHref(null))
      dispatch(publicLinkFilesActions.setNodeHref(null))
      dispatch(publicLinkFilesActions.setItemsLoadingStatus(LOADING_STATUSES.LOADING))
      dispatch(filesActions.resetState({ isActiveSearch: true }))
    }
  }, [])

  useEffect(() => {
    if (!nodesHref) {
      return
    }

    const id = paths.length > 1 ? paths[paths.length - 1] : ''

    dispatch(fetchFilesPublicLinkItems({ id }))
  }, [paths[paths.length - 1], nodesHref])

  useEffect(() => {
    if (status === LOADING_STATUSES.LOADING) {
      return
    }

    const paths = parsePathFromParams(params, 'drive')

    dispatch(fetchPublicLinkWithMeBreadcrumbsThunk({ paths }))
  }, [params['*'], status])

  const handleCancelSelection = useCallback(() => {
    dispatch(publicLinkFilesActions.unselectAll())
  }, [])

  const sizeQuotaExceedNotification = useRef(null)
  const isSizeQuotaExceedError = getIsSizeQuotaExceedErrorSelector()

  useEffect(() => {
    if (isSizeQuotaExceedError && !sizeQuotaExceedNotification.current) {
      const notificationOptions = {
        isPermanent: true,
        type: NOTIFICATION_TYPES.WARNING,
        title: t('l_notification_spaceError'),
        message: t('l_notification_copyError'),
        typeError: 'SizeQuotaExceeded',
        callback: () => {
          hideNotification(sizeQuotaExceedNotification.current)
          sizeQuotaExceedNotification.current = null
          goToSubscriptionsPage()
        }
      }

      sizeQuotaExceedNotification.current = showNotification(notificationOptions)
    }
  }, [isSizeQuotaExceedError])

  const getPlaceholderProps = (error) => {
    switch (error) {
    case FilesPublicLinksErrorTypes.NOT_EXIST:
      return {
        texts: {
          title: t('l_common_linkNotExist'),
          description: t('l_common_linkExpiredMessage'),
          btn: t('a_common_backToMyFiles')
        },
        btnType: PublicLinkPlaceholderBtnTypes.SECONDARY,
        onButtonClick: () => window.location.href = '/'
      }
    case FilesPublicLinksErrorTypes.EMPTY_FOLDER:
      return {
        texts: {
          title: t('l_common_driveEmptyPlaceholderTitle'),
          description: permission === 'write' ? t('l_common_driveEmptyPlaceholderMessage') : t('l_publicLinks_filesCanNotBeAdded'),
          btn: permission === 'write' ? t('a_files_uploadFiles') : t('l_common_backToShWithMe')
        },
        btnType: PublicLinkPlaceholderBtnTypes.SECONDARY,
        onButtonClick: () => window.location.href = '/'
      }
    case FilesPublicLinksErrorTypes.SOMETHING_WENT_WRONG:
      return {
        texts: {
          title: t('l_common_linkExpired'),
          description: t('l_common_linkExpiredMessage'),
          btn: t('a_common_backToMyFiles')
        },
        btnType: PublicLinkPlaceholderBtnTypes.SECONDARY,
        onButtonClick: () => window.location.href = '/'
      }
    case FilesPublicLinksErrorTypes.NO_ACCESS:
      return {
        texts: {
          title: t('l_common_noAccessTitle'),
          description: t('l_common_noAccessMesage'),
          btn: t('a_common_backToMyFiles'),
        },
        btnType: PublicLinkPlaceholderBtnTypes.SECONDARY,
        onButtonClick: () => window.location.href = '/' ,
      }
    }
  }

  function handleBreadcrumbClick(item) {
    const paths = parsePathFromParams(params, 'drive')
    const indexOfPath = paths.findIndex(path => path === item.id)

    dispatch(publicLinkFilesActions.unselectAll())

    if (!item.name) {
      navigate('/drive/shared-with-me')
      dispatch(filesActions.setActiveSearch(true))
      dispatch(filesActions.setSearchType({ name: t('a_photos_searchSharedToMe'), iconName: 'shared_to_me', type: 'shared_with_me' }))
      return
    }

    navigate(`${paths.slice(0, indexOfPath + 1).join('/')}`)
  }

  return (
    <>
      {status !== LOADING_STATUSES.LOADING && !error && (
        <PageHeaderBox>
          <StyledPageTopbar
            title={t('a_photos_searchSharedToMe')}
            selectedText={isMobile ? t('l_common_selectedObjects', { number: selectedItemsCount }) : t('l_files_selectedFiles', { number: selectedItemsCount })}
            selectedItemsCount={selectedItemsCount}
            onCancelSelection={handleCancelSelection}
            className={classNames({ 'with-bigger-height': !!selectedItemsCount })}
          />

          {
            breadcrumbs.length >= 1 && !selectedItemsCount && (
              <FilesTableBreadcrumbsWrapper>
                <FilesTableBreadcrumbs
                  widthWindow={widthWindow}
                  filePath={breadcrumbs}
                  firstItem={t('a_photos_searchSharedToMe')}
                  onClickedPath={handleBreadcrumbClick}
                />
              </FilesTableBreadcrumbsWrapper>
            )}
        </PageHeaderBox>
      )}

      {!!error && (
        <PublicLinkPlaceholder {...getPlaceholderProps(error)} />
      )}

      {
        status !== LOADING_STATUSES.LOADING && !error && (
          <PublicFiles />
        )
      }

      {
        status === LOADING_STATUSES.LOADING && !error && (
          <FilesSkeleton />
        )
      }

      <PublicLinkPasswordModal type={SDK_TYPES.FILES} />

      <MobileToolbarMenuToggle iconName="more_horiz"
        className="js-mobile-toolbar-menu-toggle"
      />
    </>
  )
}

const FilesTableBreadcrumbsWrapper = styled.div`
  padding-left: 60px;
  @media (min-width: ${STYLED_VARIABLES.BREAKPOINTS.LAPTOP}) {
    padding-left: 0;
  }
`

const PageHeaderBox = styled.div`
  display: flex;
  flex-flow: column;
  margin-bottom: 20px;

  @media (max-width: ${STYLED_VARIABLES.BREAKPOINTS.PHONE_100}) {
    margin: 0;
  }
`

const StyledPageTopbar = styled(PageTopbar)`
  && {
    min-height: 40px;
  }
`

export default FilesPage
